import classnames from "classnames";
import { debounce, sortedUniq } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { RequestedLoanAmountValidation } from "@krea/customer-features/form-validation/RequestedLoanAmountValidation";
import { useFormatCurrency } from "@krea/customer-features/hooks";
import {
  requestedRealEstateLoanAmountValuesDefault,
  requestedRealEstateLoanAmountValuesLarge,
  requestedLoanAmountValuesDefault,
  requestedLoanAmountValuesLarge,
} from "@krea/customer-features/services/formService";
import { settings } from "@krea/customer-features/settings";
import FieldRequestPreviewHeader from "@krea/customer-features/shared-components/field-request/FieldRequestPreviewHeader";
import FieldRequestSaved from "@krea/customer-features/shared-components/field-request/FieldRequestSaved";
import { Error } from "@krea/customer-features/shared-components/fields/error";
import { Slider } from "@krea/customer-features/shared-components/fields/slider";
import { FormatNumber } from "@krea/customer-features/shared-components/loan-application-form/Formatters";
import { usePostFieldRequest } from "@krea/customer-features/store/field-requests/hooks/mutations";
import { useGetOneFieldRequest } from "@krea/customer-features/store/field-requests/hooks/queries";
import { amountValuesList } from "@krea/customer-features/utils";
import { CountryCode } from "@krea/shared/types/common";

const RequestedAmountForm = ({
  fieldRequestID,
  isPreviewMode,
  isRealEstate,
  withInputLabel,
}) => {
  const { t } = useTranslation();
  const [formattedCurrencyValue, currencyValue] = useFormatCurrency({
    currency: settings.currency,
  });
  const { data: { submittedData, displayName } = {} } =
    useGetOneFieldRequest(fieldRequestID);
  const { mutate: postFieldRequest, error } =
    usePostFieldRequest(fieldRequestID);

  const errors = error?.data.errors || [];
  const sliderValuesLoan = sortedUniq(
    requestedLoanAmountValuesDefault.concat(requestedLoanAmountValuesLarge),
  ).filter((it) => it !== 0);

  const sliderValuesRealEstate = sortedUniq(
    requestedRealEstateLoanAmountValuesDefault.concat(
      requestedRealEstateLoanAmountValuesLarge,
    ),
  ).filter((it) => it !== 0);

  const amountRatio = (countryCode) => {
    switch (countryCode) {
      case CountryCode.SE:
        return 1;
      case CountryCode.FI:
        return 10;
      default:
        return null;
    }
  };

  const sliderValues = amountValuesList(
    !isRealEstate ? sliderValuesLoan : sliderValuesRealEstate,
    amountRatio(settings.countryCode),
  );

  const initialSliderValue = parseInt(
    submittedData?.requested_loan_amount || 500000,
  );

  const [value, setValue] = useState(initialSliderValue);
  const [validationError, setValidationError] = useState("");
  const [touched, setTouched] = useState(false);
  const [isCustomValue, setIsCustomValue] = useState(
    !sliderValues.includes(initialSliderValue),
  );

  const onPostValueFn = (requestedLoanAmount) => {
    try {
      const validAmount =
        RequestedLoanAmountValidation().validateSync(requestedLoanAmount);
      postFieldRequest({ requested_loan_amount: validAmount });
    } catch (error) {}
  };

  const onBlur = (value, touched) => {
    if (touched) {
      setTouched(false);

      if (value !== "") {
        onPostValueFn(value === "" ? null : value);
      }
    }
  };

  const debounceFunc = debounce(
    (value, touched) => onBlur(value, touched),
    1000,
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSave = useCallback(
    (value, touched) => debounceFunc(value, touched),
    [],
  );

  useEffect(() => {
    debouncedSave(value, touched);

    return () => {
      debounceFunc.cancel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, touched]);

  const numberClosestToValue = (numbers, value) => {
    const closest = numbers.reduce((prev, curr) => {
      return Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev;
    });

    return closest;
  };

  const setValidatedValue = (value) => {
    try {
      const validatedValue =
        RequestedLoanAmountValidation().validateSync(value);
      setValue(validatedValue);
      setValidationError("");
    } catch (error) {
      setValue(value);
      const validationError = error?.errors[0];
      setValidationError(validationError);
    }
  };

  const onCustomValueToggle = () => {
    if (isCustomValue) {
      const closestNumber = numberClosestToValue(sliderValues, value);

      const isTouched = value !== closestNumber;
      setTouched(isTouched);
      setValidatedValue(closestNumber);
    }

    setIsCustomValue((prevState) => !prevState);
  };

  const onSliderChange = ([value]) => {
    const newSliderValue = sliderValues[value];

    const isTouched = value !== newSliderValue;
    setTouched(isTouched);
    setValidatedValue(newSliderValue);
  };

  const onChange = ({ target }) => {
    const newValue = target.value.replace(/\s+/g, "");
    const isTouched = newValue !== value;
    setTouched(isTouched);

    // if newValue is not NaN

    setValidatedValue(newValue ? newValue : "");
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onPostValueFn(value === "" ? null : value);
    }
  };

  // Same as Formatters.FormatCurrency but hacked to show "Välj belopp" instead of 0
  const CustomFormatCurrency = (value, formattedCurrencyValue) => {
    if (value === "" || value === 0) {
      return t("applicationForm.commons.requestedAmountLabel");
    }

    const formattedValue = formattedCurrencyValue(value);

    return formattedValue ? formattedValue.replace(/\./g, " ") : formattedValue; // edge formats with dots instead of spaces
  };

  const formClasses = classnames("input-form", {
    "input-form-padded": isPreviewMode,
  });

  return (
    <>
      {isPreviewMode ? (
        <FieldRequestPreviewHeader
          label={displayName}
          value={
            <>
              {value && (
                <>{CustomFormatCurrency(value, formattedCurrencyValue)}</>
              )}
            </>
          }
        />
      ) : (
        <>
          <form
            className={formClasses}
            onKeyDown={onKeyDown}
            onBlur={() => onBlur(value, touched)}
            // onSubmit={(e) => e.preventDefault()}
          >
            <Slider
              id="loan-amount-slider"
              label={withInputLabel ? <>{displayName}</> : null}
              name="requested_loan_amount"
              htmlFor="requestedLoanAmount"
              labelClassName="align-items-center"
              onSliderChange={onSliderChange}
              onValueChange={onChange}
              value={value}
              values={sliderValues}
              formatter={() =>
                CustomFormatCurrency(value, formattedCurrencyValue)
              }
              inputFormatter={FormatNumber}
              placeholder={t(
                "applicationForm.commons.exactRequestedAmountPlaceholder",
                {
                  currency: settings.currency,
                },
              )}
              postfix={value !== "" ? currencyValue : null}
              isCustomInput={isCustomValue}
              onCustomToggle={onCustomValueToggle}
              // error={touched ? errors?.[0]?.messages : null}
              error={validationError}
            />

            <FieldRequestSaved fieldRequestID={fieldRequestID} />
          </form>

          {errors.map((error, index) => (
            <Error className="mt-3" key={index}>
              {error.message}
            </Error>
          ))}
        </>
      )}
    </>
  );
};

export default RequestedAmountForm;
